import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
/* eslint-disable @typescript-eslint/no-shadow */
import { toast } from '@livable/saas-react-components';
import { getParent, types } from 'mobx-state-tree';
import { track } from 'src/configs/amplitude';
import { httpClientCancelTokenStatic, isHttpClientCancel } from 'src/core/services/api/httpClient';
import { generateServerResponseErrorText } from '../serverErrorValidation';
import { parseJwt } from 'src/core/services/jwt';
import { AUTH_TOKEN_KEYS } from 'src/constants/authToken';
import { isLivableStaffFromToken } from 'src/utils/isLivableStaffFromToken';
// import { TAsyncActionOptions } from 'src/core/services/api/utils';

const asyncModel = types.model('async', {
  inProgress: false,
  inProgressUnicIds: types.maybeNull(types.array(types.string)),
  inProgressRetry: false,
  error: types.optional(types.frozen(), null),
  hasEverBeenRun: false,
  throwable: false,
  // Additive background lookups set this. Their failure must leave the page usable and must not
  // raise a global toast: the caller renders its own fallback. A try/catch at the call site cannot
  // do this, because the toast is raised here before run() resolves.
  silent: false
}).views(store => ({
  get errorMessage() {
    if (store.error && store.error.message) {
      return store.error.message;
    }
    return null;
  },
  get isError() {
    return !!store.error;
  },
  get canBeRun() {
    return !store.error && !store.inProgress;
  },
  get inProgressAgain() {
    return store.inProgress && store.hasEverBeenRun;
  }
})).actions(store => ({
  start() {
    let retry = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    if (retry) {
      store.inProgressRetry = true;
    } else {
      store.inProgress = true;
    }
    store.error = null;
  },
  success() {
    if (!store.hasEverBeenRun) {
      store.hasEverBeenRun = true;
    }
    if (store.inProgressRetry) {
      store.inProgressRetry = false;
    } else {
      store.inProgress = false;
    }
  },
  failed(err, throwError) {
    var _response$request$res, _response$request, _response$request$res2, _response$request2, _response$request$res3, _response$request3, _response$status2, _response$headers$cor, _response$headers2, _response$data$Error$2, _response$data0;
    let quiet = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    const isSilent = store.silent || quiet;
    if (!store.hasEverBeenRun) {
      store.hasEverBeenRun = true;
    }
    if (store.inProgressRetry) {
      store.inProgressRetry = false;
    } else {
      store.inProgress = false;
    }
    const response = err === null || err === void 0 ? void 0 : err.response;
    if (response) {
      var _ref, _response$data$Error, _response$data, _response$data2, _response$data3, _response$data$isSucc, _response$data4, _response$status, _response$statusText, _response$data$meta, _response$data5, _ref2, _response$data$Error$, _response$data6, _response$data7, _response$data8;
      // `message` last: that is the API gateway's shape for a route it does not know, so a call to
      // an endpoint the backend has not deployed yet reports the gateway's reason instead of "Error".
      //
      // A bare string is staff-only. The `Error` envelope is the platform's customer-facing
      // contract and every user sees it, but `error`/`message` as a plain string carries operator
      // diagnostics ("storage is not configured for this environment") that mean nothing to a
      // property manager and disclose how the estate is built. They keep the generic text and the
      // correlation id, which is what support asks for anyway.
      const raw = (_ref = (_response$data$Error = response === null || response === void 0 || (_response$data = response.data) === null || _response$data === void 0 ? void 0 : _response$data.Error) !== null && _response$data$Error !== void 0 ? _response$data$Error : response === null || response === void 0 || (_response$data2 = response.data) === null || _response$data2 === void 0 ? void 0 : _response$data2.error) !== null && _ref !== void 0 ? _ref : response === null || response === void 0 || (_response$data3 = response.data) === null || _response$data3 === void 0 ? void 0 : _response$data3.message;
      const details = typeof raw === 'string' && !isLivableStaffFromToken() ? null : raw;
      store.error = {
        isSuccessful: (_response$data$isSucc = response === null || response === void 0 || (_response$data4 = response.data) === null || _response$data4 === void 0 ? void 0 : _response$data4.isSuccessful) !== null && _response$data$isSucc !== void 0 ? _response$data$isSucc : null,
        status: (_response$status = response === null || response === void 0 ? void 0 : response.status) !== null && _response$status !== void 0 ? _response$status : null,
        statusText: (_response$statusText = response === null || response === void 0 ? void 0 : response.statusText) !== null && _response$statusText !== void 0 ? _response$statusText : null,
        meta: (_response$data$meta = response === null || response === void 0 || (_response$data5 = response.data) === null || _response$data5 === void 0 ? void 0 : _response$data5.meta) !== null && _response$data$meta !== void 0 ? _response$data$meta : null,
        errorCode: (_ref2 = (_response$data$Error$ = response === null || response === void 0 || (_response$data6 = response.data) === null || _response$data6 === void 0 || (_response$data6 = _response$data6.Error) === null || _response$data6 === void 0 ? void 0 : _response$data6.ErrorCode) !== null && _response$data$Error$ !== void 0 ? _response$data$Error$ : response === null || response === void 0 || (_response$data7 = response.data) === null || _response$data7 === void 0 || (_response$data7 = _response$data7.error) === null || _response$data7 === void 0 ? void 0 : _response$data7.errorCode) !== null && _ref2 !== void 0 ? _ref2 : null,
        errorResp: generateServerResponseErrorText(response === null || response === void 0 || (_response$data8 = response.data) === null || _response$data8 === void 0 ? void 0 : _response$data8.errors, details)
      };
      if (!isSilent) {
        var _response$data9, _response$headers;
        toast('error', generateServerResponseErrorText(response === null || response === void 0 || (_response$data9 = response.data) === null || _response$data9 === void 0 ? void 0 : _response$data9.errors, details, (response === null || response === void 0 ? void 0 : response.status) === 400 ? null : response === null || response === void 0 || (_response$headers = response.headers) === null || _response$headers === void 0 ? void 0 : _response$headers['correlation-id']), (response === null || response === void 0 ? void 0 : response.status) === 400 ? undefined : {
          autoClose: false
        });
      }

      // Telemetry gets the untruncated, ungated value. Bounding what a user reads is not a reason
      // to lose the reason from the logs support actually reads.
      track('api-error', {
        body: raw
      });
    } else {
      const e = new Error(err);
      store.error = err;
      if (!isSilent) toast('error', {
        title: 'Error',
        body: [e.message]
      });
      track('api-error', {
        body: e.message
      });
    }
    if (window.location.hostname === 'localhost') return;
    let accId = null;
    if (!!localStorage.getItem(AUTH_TOKEN_KEYS.accessToken)) {
      const accessToken = parseJwt(localStorage.getItem(AUTH_TOKEN_KEYS.accessToken));
      if (accessToken && accessToken['custom:accountid']) {
        accId = accessToken['custom:accountid'];
      }
    }
    const body = {
      accountId: accId,
      response: (_response$request$res = response === null || response === void 0 || (_response$request = response.request) === null || _response$request === void 0 ? void 0 : _response$request.response) !== null && _response$request$res !== void 0 ? _response$request$res : null,
      responseText: (_response$request$res2 = response === null || response === void 0 || (_response$request2 = response.request) === null || _response$request2 === void 0 ? void 0 : _response$request2.responseText) !== null && _response$request$res2 !== void 0 ? _response$request$res2 : null,
      responseUrl: (_response$request$res3 = response === null || response === void 0 || (_response$request3 = response.request) === null || _response$request3 === void 0 ? void 0 : _response$request3.responseUrl) !== null && _response$request$res3 !== void 0 ? _response$request$res3 : null,
      statusCode: (_response$status2 = response === null || response === void 0 ? void 0 : response.status) !== null && _response$status2 !== void 0 ? _response$status2 : null,
      correlationId: (_response$headers$cor = response === null || response === void 0 || (_response$headers2 = response.headers) === null || _response$headers2 === void 0 ? void 0 : _response$headers2['correlation-id']) !== null && _response$headers$cor !== void 0 ? _response$headers$cor : null,
      consoleErrorMessage: (_response$data$Error$2 = response === null || response === void 0 || (_response$data0 = response.data) === null || _response$data0 === void 0 || (_response$data0 = _response$data0.Error) === null || _response$data0 === void 0 ? void 0 : _response$data0.GeneralErrorMessage) !== null && _response$data$Error$2 !== void 0 ? _response$data$Error$2 : null,
      timestamp: new Date(),
      windowUrl: window.location.href,
      applicationName: 'PM'
    };
    const xhr = new XMLHttpRequest();
    xhr.open('POST', "".concat(process.env.ACTIVITY_API_ENDPOINT, "/Monitoring/client-error"), true);
    xhr.setRequestHeader('Content-type', 'application/json');
    xhr.addEventListener('load', () => {
      // let data = resp.currentTarget.response;
      // data = JSON.parse(data);
    });
    xhr.send(JSON.stringify(body));
  },
  clearReqUnicIds() {
    store.inProgressUnicIds = null;
  },
  throwError(value) {
    store.throwable = value;
  }
}));
export function createAsync(thunk) {
  let auto = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
  let throwError = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : auto;
  let silent = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : false;
  const thunkModel = asyncModel.named('thunkModel').actions(store => {
    let cancelTokenSource;
    return {
      async auto(promise) {
        let quiet = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
        try {
          store.start();
          const result = await promise();
          store.success();
          store.clearReqUnicIds();
          return result;
        } catch (error) {
          if (!isHttpClientCancel(error)) {
            store.failed(error, throwError, quiet);
          }
          store.clearReqUnicIds();
        }
        return undefined;
      },
      /**
       * `runOptions.silent` quiets this one call the way `createSilentAsync` quiets every call: the failure
       * is recorded and no toast is raised. It is for a background re-read of what the page also reads in the
       * foreground, which has to stay the same action so that each cancels the other.
       */
      run(args, unicIds, runOptions) {
        const thunkFn = thunk(args);
        store.inProgressUnicIds = unicIds;
        if (cancelTokenSource) {
          cancelTokenSource.cancel('CANCEL_REQUEST');
        }
        cancelTokenSource = httpClientCancelTokenStatic.source();
        let promise;
        if (Array.isArray(thunkFn)) {
          const _thunkFn = _slicedToArray(thunkFn, 1),
            actualThunk = _thunkFn[0];
          promise = () => actualThunk.bind(getParent(store))({
            cancelToken: {
              cancelToken: cancelTokenSource.token
            }
          }, store);
        } else {
          promise = () => thunkFn.bind(getParent(store))({
            cancelToken: {
              cancelToken: cancelTokenSource.token
            }
          }, store);
        }
        if (auto) {
          return this.auto(promise, !!(runOptions !== null && runOptions !== void 0 && runOptions.silent));
        }
        return promise();
      }
    };
  });
  return types.optional(thunkModel, {
    silent
  });
}

/**
 * An additive lookup: records the failure on the model, raises no global toast.
 *
 * For requests that decorate a page whose primary job is something else, where the caller renders
 * its own fallback and the user can keep working. `isError` still populates, so a caller can tell
 * "the request failed" from "there is nothing to show".
 */
export function createSilentAsync(thunk) {
  return createAsync(thunk, true, true, true);
}